import { reportDraftDb } from "./reportDraftDb";

const draftKey = (visitId, reportType) => `${visitId}:${reportType}`;

export class ReportDraftKernel {
	constructor({ apiBase = "/api", csrfToken = null } = {}) {
		this.apiBase = apiBase;
		this.csrfToken = csrfToken;
	}

	async saveDraft(visitId, reportType, payload, meta = {}) {
		await reportDraftDb.putDraft({
			key: draftKey(visitId, reportType),
			visitId,
			reportType,
			payload,
			meta,
			syncState: "pending",
			schemaVersion: 1,
		});
		// Dedup: no máximo um draft_deposit por (visitId, reportType) na fila. O
		// depósito lê sempre o rascunho atual da store, por isso saves repetidos
		// offline não devem acumular ops (inflava o contador de sincronização).
		await reportDraftDb.dequeueDraftDeposit(visitId, reportType);
		await reportDraftDb.enqueue({ type: "draft_deposit", visitId, reportType });
		return this.syncAll().catch(() => {});
	}

	async loadDraft(visitId, reportType) {
		const local = await reportDraftDb.getDraft(draftKey(visitId, reportType));
		if (local) return local.payload;
		// fallback server (hidratação cross-device)
		const res = await fetch(`${this.apiBase}/visits/${visitId}/report-drafts/${reportType}`, {
			headers: {
				Accept: "application/json",
				...(this.csrfToken ? { "X-CSRF-TOKEN": this.csrfToken } : {}),
			},
		});
		if (!res.ok) return null;
		const json = await res.json();
		return json.draft ? json.draft.payload : null;
	}

	async capturePhoto(visitId, reportType, blob, meta = {}) {
		const photoUuid = crypto.randomUUID();
		await reportDraftDb.putPhoto({ photoUuid, visitId, reportType, blob, meta, syncState: "pending" });
		await reportDraftDb.enqueue({ type: "photo_upload", visitId, reportType, photoUuid });
		return photoUuid;
	}

	async syncAll() {
		// O sync_queue é partilhado com o TurnKernel do assistente (BEL-1578
		// Etapa 2). Filtra fora as ops kind:'assistant' — só o TurnKernel.syncAll
		// as drena (endpoints do assistente). Ops manuais não têm kind, logo
		// op.kind !== "assistant" é true para elas (sem regressão).
		const queue = (await reportDraftDb.allQueue()).filter(op => op.kind !== "assistant");
		// Depositar o rascunho ANTES de carregar fotos, para o confirm marcar as fotos
		// numa linha report_drafts já existente (BEL-1578).
		queue.sort((a, b) => (a.type === "draft_deposit" ? 0 : 1) - (b.type === "draft_deposit" ? 0 : 1));
		for (const op of queue) {
			try {
				if (op.type === "photo_upload") {
					await this._uploadPhoto(op);
				} else if (op.type === "draft_deposit") {
					await this._depositDraft(op);
				}
				await reportDraftDb.dequeue(op.id);
			} catch (e) {
				if (e && e.status >= 400 && e.status < 500) {
					await reportDraftDb.dequeue(op.id); // 4xx → dead-letter, não retry (lição BEL-1551)
				}
				// 5xx/rede: deixa na fila para retry
			}
		}
	}

	async _uploadPhoto(op) {
		const photo = await reportDraftDb.getPhoto(op.photoUuid);
		if (!photo) return;
		const presign = await this._post(`/visits/${op.visitId}/report-drafts/photos/presign`, {
			reportType: op.reportType,
			photoUuids: [op.photoUuid],
		});
		const upload = presign.uploads[0];
		const put = await fetch(upload.url, {
			method: "PUT",
			body: photo.blob,
			headers: upload.headers || {},
		});
		if (!put.ok) throw { status: put.status };
		await this._post(`/visits/${op.visitId}/report-drafts/photos/confirm`, {
			reportType: op.reportType,
			photoUuids: [op.photoUuid],
		});
		await reportDraftDb.deletePhoto(op.photoUuid);
	}

	async _depositDraft(op) {
		const local = await reportDraftDb.getDraft(draftKey(op.visitId, op.reportType));
		if (!local) return;
		await this._post(`/visits/${op.visitId}/report-drafts/${op.reportType}`, {
			payload: local.payload,
			geo: local.meta.geo || null,
			deviceMeta: local.meta.deviceMeta || null,
			capturedAt: local.meta.capturedAt || null,
		});
		local.syncState = "confirmed";
		await reportDraftDb.putDraft(local);
	}

	/**
	 * Deposit a draft payload to the server synchronously and awaitably, surfacing
	 * failures to the caller (unlike saveDraft → syncAll, which swallows errors).
	 *
	 * After Fase D the manual report store() reads report_drafts server-side and
	 * ignores posted answers, so the submit flow must flush the current form
	 * state here BEFORE calling store — otherwise an unsynced async deposit makes
	 * store 422 with empty_draft. Uses the same authenticated path (CSRF header +
	 * laravel_token cookie) as _depositDraft.
	 *
	 * @throws {{status:number}} when the server rejects the deposit
	 */
	async depositNow(visitId, reportType, payload, meta = {}) {
		return this._post(`/visits/${visitId}/report-drafts/${reportType}`, {
			payload,
			geo: meta.geo || null,
			deviceMeta: meta.deviceMeta || null,
			capturedAt: meta.capturedAt || null,
		});
	}

	async _post(path, body) {
		const res = await fetch(`${this.apiBase}${path}`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Accept: "application/json",
				...(this.csrfToken ? { "X-CSRF-TOKEN": this.csrfToken } : {}),
			},
			body: JSON.stringify(body),
		});
		if (!res.ok) throw { status: res.status };
		return res.json();
	}

	async pendingCount() {
		return reportDraftDb.countPending();
	}

	async purge(visitId, reportType) {
		await reportDraftDb.deleteDraft(draftKey(visitId, reportType));
	}

	/**
	 * Apaga o rascunho NO SERVIDOR (report_drafts) além do local. Usado pelo
	 * "Apagar rascunho" dos formulários: sem isto, em janela de conferência a
	 * visita nunca sairia de "por conferir". Best-effort: offline ou em erro
	 * devolve false (o chamador grava um tombstone e retenta mais tarde; o TTL
	 * server-side cobre o resto).
	 *
	 * @returns {Promise<boolean>} true quando o servidor confirmou o delete
	 */
	async deleteServerDraft(visitId, reportType) {
		return fetch(`${this.apiBase}/visits/${visitId}/report-drafts/${reportType}`, {
			method: "DELETE",
			headers: {
				Accept: "application/json",
				...(this.csrfToken ? { "X-CSRF-TOKEN": this.csrfToken } : {}),
			},
		})
			.then(res => res.ok)
			.catch(() => false);
	}

	async purgeVisit(visitId) {
		await reportDraftDb.deleteByVisit(visitId);
	}

	async removePhoto(photoUuid) {
		await reportDraftDb.deletePhoto(photoUuid);
		await reportDraftDb.dequeueByPhoto(photoUuid);
	}

	async getPhoto(photoUuid) {
		return reportDraftDb.getPhoto(photoUuid);
	}

	async photoPreviews(visitId, reportType, photoUuids) {
		if (!photoUuids || photoUuids.length === 0) {
			return [];
		}
		const res = await this._post(`/visits/${visitId}/report-drafts/photos/previews`, { reportType, photoUuids });
		return res.previews || [];
	}
}
